import React from "react";

import { Marker, Polyline } from "@react-google-maps/api";
import { useTranslation } from "react-i18next";

import { Coordinates, ShipmentPosition, StatusProgress } from "../../../models/marine.traffic";
import { VoyageMap, VoyageMapInformationProps } from "../../VoyageDetailsComponent/Map";
import { SectionTitle } from "../styles";
import { Wrapper, Text, PositionWrapper } from "./styles";
import { colors } from "@zapalacorp/tokens";

const PLANE_COMPLETED = require("../../../assets/img/plane-completed.svg");
const PLANE_PREDICTED_ICON = require("../../../assets/img/plane-predicted.svg");
const SHIP_HOME_DONE = require("../../../assets/img/ship-home-done.svg");
const SHIP_HOME = require("../../../assets/img/ship-home.svg");
const SHIP_MARKER_DONE = require("../../../assets/img/ship-icon-done.svg");
const SHIP_MARKER = require("../../../assets/img/ship-marker.svg");

interface Props {
  lastContainerPosition: VoyageMapInformationProps | undefined;
  allPositions?: Coordinates[];
  view?: boolean;
  positions?: ShipmentPosition;
  statusProgressBar?: StatusProgress;
  externalMapIsLoaded?: boolean;
  isAirTracking?: boolean;
  isCustomerPortal?: boolean;
}

const LivePosition = ({
  allPositions,
  lastContainerPosition,
  view,
  statusProgressBar,
  positions,
  externalMapIsLoaded,
  isAirTracking,
  isCustomerPortal,
}: Props) => {
  const { t: translate } = useTranslation();

  const getMakerFromPosition = (index: number): string => {
    if (index === 0) {
      if (isAirTracking) return PLANE_COMPLETED;
      if (
        statusProgressBar?.activeStep &&
        (statusProgressBar?.activeStep > index || statusProgressBar?.activeStep === -1)
      )
        return SHIP_HOME_DONE;

      return SHIP_HOME;
    }
    if (isAirTracking) {
      if (
        statusProgressBar?.activeStep &&
        (statusProgressBar?.activeStep > index || statusProgressBar?.activeStep === -1)
      ) {
        return PLANE_COMPLETED;
      }

      return PLANE_PREDICTED_ICON;
    }
    if (
      statusProgressBar?.activeStep &&
      (statusProgressBar?.activeStep > index || statusProgressBar?.activeStep === -1)
    )
      return SHIP_MARKER_DONE;

    return SHIP_MARKER;
  };

  return (
    <PositionWrapper>
      <Wrapper>
        <SectionTitle>{isAirTracking ? translate("map") : translate("livePosition")}</SectionTitle>
      </Wrapper>
      <VoyageMap
        information={lastContainerPosition}
        loadingMap={false}
        showLivePosition={view}
        externalMapIsLoaded={externalMapIsLoaded}
        isAirTracking={isAirTracking}
        isCustomerPortal={isCustomerPortal}
      >
        {view ? (
          <>
            {allPositions?.map((position, index: number) => (
              <Marker
                key={`position-${index}`}
                position={position}
                icon={{
                  url: getMakerFromPosition(index),
                  scaledSize: new google.maps.Size(30, 30),
                }}
              />
            ))}
            <Polyline
              path={statusProgressBar?.activeStep === -1 ? [] : positions?.positionsInProgress}
              options={{
                geodesic: true,
                strokeOpacity: 0,
                icons: [
                  {
                    icon: {
                      path: "M 0,-1 0,1",
                      strokeOpacity: 1,
                      strokeColor: isAirTracking ? colors.gray600 : "#0055A4",
                      scale: 4,
                    },
                    offset: "0",
                    repeat: "20px",
                  },
                ],
              }}
            />
            <Polyline
              path={
                statusProgressBar?.activeStep === -1 ? allPositions : positions?.positionsConclude
              }
              options={{
                geodesic: true,
                strokeOpacity: 1,
                strokeColor: isAirTracking ? colors.brandBlue600 : "#039855",
                strokeWeight: 4,
              }}
            />
          </>
        ) : null}
      </VoyageMap>
      <Wrapper>
        {isAirTracking ? (
          <Text>{translate("theAirplanePositionIsUpdatedFrom15To15Minutes")}</Text>
        ) : (
          <Text>{`${translate("vesselPositionUpdateDailyAt5pmUTC")}. ${translate(
            "locationIndicatedAboveIsMoreAccurateIfItDiffersFromMapDisplay"
          )}.`}</Text>
        )}
      </Wrapper>
    </PositionWrapper>
  );
};

export default LivePosition;
